import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, hasSession, setTokens } from "@/lib/api";
import type { Permission } from "@/lib/permissions";
import type { Account, Address, Session, Tokens, User } from "@/lib/types";

const ADMIN_STASH_KEY = "weship.admin-session";

export interface RegisterPayload {
  account_type: "individual" | "business";
  company_name?: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  password: string;
  /**
   * Where this account's parcels are collected from. Captured at signup rather
   * than at the first booking: WeShip books every tenant through one carrier
   * account, so the collection address is the only thing on the payload that
   * says whose premises to visit.
   */
  collection_address: Address;
}

interface MeResponse {
  account: Account;
  user?: User;
  role: string;
  role_label: string;
  is_staff: boolean;
  permissions: string[];
  impersonation?: { active: boolean; impersonator_id: string };
}

interface AuthValue {
  account: Account | null;
  user: User | null;
  role: string;
  roleLabel: string;
  isStaff: boolean;
  permissions: Set<string>;
  /** The single authorisation question the whole interface asks. */
  can: (permission: Permission) => boolean;
  canAny: (...permissions: Permission[]) => boolean;
  impersonating: boolean;
  ready: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  adoptSession: (session: Session) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<void>;
  signOut: () => void;
  startImpersonation: (userId: string, reason: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [ready, setReady] = useState(false);

  const load = useCallback(async () => {
    const next = await api.get<MeResponse>("/v1/me");
    setMe(next);
    return next;
  }, []);

  const signOut = useCallback(() => {
    setTokens(null);
    sessionStorage.removeItem(ADMIN_STASH_KEY);
    setMe(null);
  }, []);

  // Restore the session on load so a refresh does not dump the user at login.
  useEffect(() => {
    if (!hasSession()) {
      setReady(true);
      return;
    }
    let cancelled = false;
    load()
      .catch(() => signOut())
      .finally(() => !cancelled && setReady(true));
    return () => {
      cancelled = true;
    };
  }, [load, signOut]);

  // The API client raises this when a refresh token is finally rejected.
  useEffect(() => {
    const handler = () => signOut();
    window.addEventListener("weship:signed-out", handler);
    return () => window.removeEventListener("weship:signed-out", handler);
  }, [signOut]);

  const value = useMemo<AuthValue>(() => {
    const permissions = new Set(me?.permissions ?? []);
    return {
      account: me?.account ?? null,
      user: me?.user ?? null,
      role: me?.role ?? "",
      roleLabel: me?.role_label ?? "",
      isStaff: me?.is_staff ?? false,
      permissions,
      can: (permission) => permissions.has(permission),
      canAny: (...list) => list.some((permission) => permissions.has(permission)),
      impersonating: Boolean(me?.impersonation?.active),
      ready,

      signIn: async (email, password) => {
        const session = await api.post<Session>("/v1/auth/login", { email, password });
        setTokens(session.tokens);
        await load();
      },

      /**
       * Adopt a session that arrived from somewhere other than the login form.
       *
       * Setting a password returns one, because sending somebody who has just
       * proved they control the mailbox to a login form to retype what they
       * typed a second ago is a pointless step.
       */
      adoptSession: async (session) => {
        setTokens(session.tokens);
        await load();
      },

      register: async (payload) => {
        const session = await api.post<Session>("/v1/auth/register", payload);
        setTokens(session.tokens);
        await load();
      },

      signOut,

      /**
       * Starting an impersonation stashes the admin's own tokens in
       * sessionStorage so exiting returns them to their own session rather than
       * to the login screen. sessionStorage rather than localStorage on
       * purpose: closing the tab should not leave a set of admin tokens behind
       * for the next person at that desk.
       */
      startImpersonation: async (userId, reason) => {
        const current = localStorage.getItem("weship.session");
        const result = await api.post<{ tokens: Tokens; session: { id: string } }>(
          "/v1/admin/impersonate",
          { user_id: userId, reason },
        );
        if (current) {
          sessionStorage.setItem(
            ADMIN_STASH_KEY,
            JSON.stringify({ tokens: JSON.parse(current), session_id: result.session.id }),
          );
        }
        setTokens(result.tokens);
        await load();
      },

      stopImpersonation: async () => {
        const raw = sessionStorage.getItem(ADMIN_STASH_KEY);
        const stashed = raw ? (JSON.parse(raw) as { tokens: Tokens; session_id: string }) : null;

        // Close the session at the server first, while the impersonation token
        // is still the one being presented. A failure here must not strand the
        // admin inside the customer's account, so it is deliberately ignored.
        if (stashed?.session_id) {
          await api.post(`/v1/admin/impersonate/${stashed.session_id}/end`).catch(() => undefined);
        }

        sessionStorage.removeItem(ADMIN_STASH_KEY);
        if (!stashed) {
          signOut();
          return;
        }
        setTokens(stashed.tokens);
        await load();
      },
    };
  }, [me, ready, load, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
